// All formatters are hand-rolled so the server and the browser always produce identical text.

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// Indian digit grouping: 1,24,500
const groupIndian = (value: number): string => {
    const negative = value < 0
    const digits = String(Math.abs(Math.round(value)))

    if (digits.length <= 3) return `${negative ? '-' : ''}${digits}`

    const lastThree = digits.slice(-3)
    let rest = digits.slice(0, -3)
    const parts: Array<string> = []

    while (rest.length > 2) {
        parts.unshift(rest.slice(-2))
        rest = rest.slice(0, -2)
    }

    if (rest) parts.unshift(rest)

    return `${negative ? '-' : ''}${parts.join(',')},${lastThree}`
}

export const formatNumber = (value: number | null | undefined): string => groupIndian(value ?? 0)

export const formatINR = (amount: number | null | undefined): string => {
    const value = Math.round(amount ?? 0)

    return value < 0 ? `-₹${groupIndian(-value)}` : `₹${groupIndian(value)}`
}

const trimDecimal = (value: number): string => {
    const fixed = value >= 100 ? value.toFixed(0) : value.toFixed(1)

    return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed
}

// Short rupee amounts for chart axes and tiles: ₹950, ₹12.4k, ₹3.2L, ₹1.1Cr
export const formatCompactINR = (amount: number): string => {
    const abs = Math.abs(amount)
    const sign = amount < 0 ? '-' : ''

    if (abs >= 10000000) return `${sign}₹${trimDecimal(abs / 10000000)}Cr`
    if (abs >= 100000) return `${sign}₹${trimDecimal(abs / 100000)}L`
    if (abs >= 1000) return `${sign}₹${trimDecimal(abs / 1000)}k`

    return `${sign}₹${Math.round(abs)}`
}

export const formatPercent = (value: number, digits = 0): string => `${(Number.isFinite(value) ? value : 0).toFixed(digits)}%`

export const formatDuration = (minutes: number): string => {
    if (!minutes || minutes < 60) return `${minutes || 0} min`

    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    const hourLabel = hours === 1 ? '1 hr' : `${hours} hrs`

    return rest ? `${hourLabel} ${rest} min` : hourLabel
}

export const formatTime12 = (time: string | null | undefined): string => {
    if (!time) return ''

    const [hourText, minuteText = '00'] = time.split(':')
    const hour = Number(hourText)
    const suffix = hour < 12 ? 'AM' : 'PM'
    const displayHour = hour % 12 === 0 ? 12 : hour % 12

    return `${displayHour}:${minuteText.slice(0, 2)} ${suffix}`
}

export type DateStyle = 'short' | 'medium' | 'long' | 'full' | 'month' | 'monthLong' | 'weekday' | 'dayMonth'

// Formats a salon-local calendar date (YYYY-MM-DD) without any timezone drift.
export const formatDate = (date: string | null | undefined, style: DateStyle = 'medium'): string => {
    if (!date) return ''

    const parsed = new Date(`${date.slice(0, 10)}T00:00:00Z`)

    if (Number.isNaN(parsed.getTime())) return ''

    const day = parsed.getUTCDate()
    const month = parsed.getUTCMonth()
    const year = parsed.getUTCFullYear()
    const weekday = parsed.getUTCDay()

    switch (style) {
        case 'short':
            return `${day} ${MONTHS_SHORT[month]}`
        case 'dayMonth':
            return `${day} ${MONTHS_LONG[month]}`
        case 'long':
            return `${DAYS_LONG[weekday]}, ${day} ${MONTHS_LONG[month]} ${year}`
        case 'full':
            return `${day} ${MONTHS_SHORT[month]} ${year}`
        case 'month':
            return `${MONTHS_SHORT[month]} ${year}`
        case 'monthLong':
            return MONTHS_LONG[month]
        case 'weekday':
            return DAYS_SHORT[weekday]
        default:
            return `${DAYS_SHORT[weekday]}, ${day} ${MONTHS_SHORT[month]}`
    }
}

// Formats a UTC timestamp in salon time (IST, +05:30): 3 Nov 2026, 4:30 PM
export const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return ''

    const time = Date.parse(iso)

    if (Number.isNaN(time)) return ''

    const shifted = new Date(time + 330 * 60000).toISOString()

    return `${formatDate(shifted.slice(0, 10), 'full')}, ${formatTime12(shifted.slice(11, 16))}`
}

export const formatBirthday = (day: number | null | undefined, month: number | null | undefined): string => {
    if (!day || !month || month < 1 || month > 12) return ''

    return `${day} ${MONTHS_LONG[month - 1]}`
}

export const monthName = (month: number): string => MONTHS_LONG[(month - 1 + 12) % 12]

export const firstName = (name: string | null | undefined): string => (name ?? '').trim().split(/\s+/)[0] || 'there'

export const initials = (name: string | null | undefined): string => (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('')

export const pluralize = (count: number, singular: string, plural = `${singular}s`): string => `${formatNumber(count)} ${count === 1 ? singular : plural}`

export const escapeHtml = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
